import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { clientsApi, ClientBalanceResponse } from '../../services/clients';
import { Client, ClientStatus } from '../../types';

const STATUS_TABS: { label: string; value: ClientStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Prospects', value: 'prospect' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Archived', value: 'archived' },
];

// Helper to format cents as currency
const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

export const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [balances, setBalances] = useState<Record<string, ClientBalanceResponse>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<ClientStatus | 'all'>('all');

  useEffect(() => {
    loadClients();
  }, [activeTab]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const status = activeTab === 'all' ? undefined : activeTab;
      const data = await clientsApi.list(status);
      setClients(data.clients);
      
      // Load balances for all clients
      const balancePromises = data.clients.map(async (client) => {
        try {
          const balance = await clientsApi.getBalance(client.clientId);
          return { clientId: client.clientId, balance };
        } catch {
          return { clientId: client.clientId, balance: null };
        }
      });
      
      const balanceResults = await Promise.all(balancePromises);
      const balanceMap: Record<string, ClientBalanceResponse> = {};
      balanceResults.forEach(({ clientId, balance }) => {
        if (balance) {
          balanceMap[clientId] = balance;
        }
      });
      setBalances(balanceMap);
    } catch (err) {
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: ClientStatus) => {
    const colors: Record<ClientStatus, string> = {
      prospect: '#2196f3',
      active: '#4caf50',
      inactive: '#ff9800',
      archived: '#9e9e9e',
    };
    return (
      <span style={{
        backgroundColor: colors[status],
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        textTransform: 'capitalize',
      }}>
        {status}
      </span>
    );
  };

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ margin: 0 }}>Clients</h1>
          <button
            onClick={() => navigate('/clients/new')}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            + New Client
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              style={{
                padding: '8px 16px',
                border: 'none',
                backgroundColor: activeTab === tab.value ? '#1976d2' : 'transparent',
                color: activeTab === tab.value ? 'white' : '#666',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && <div style={{ color: 'red', marginBottom: '16px' }}>{error}</div>}

        {loading ? (
          <div>Loading...</div>
        ) : clients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#666' }}>
            No clients found. Click "New Client" to add one.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Email</th>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Phone</th>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '12px 8px' }}>Balance Due</th>
                <th style={{ textAlign: 'left', padding: '12px 8px' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(client => {
                const balance = balances[client.clientId];
                const totalBalance = balance?.totalBalance || 0;
                return (
                  <tr
                    key={client.clientId}
                    onClick={() => navigate(`/clients/${client.clientId}`)}
                    style={{ borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }}
                    onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td style={{ padding: '12px 8px' }}>{client.lastName}, {client.firstName}</td>
                    <td style={{ padding: '12px 8px' }}>{client.email || '-'}</td>
                    <td style={{ padding: '12px 8px' }}>{client.phone || '-'}</td>
                    <td style={{ padding: '12px 8px' }}>{getStatusBadge(client.status)}</td>
                    <td style={{ 
                      padding: '12px 8px', 
                      textAlign: 'right',
                      color: totalBalance > 0 ? '#d32f2f' : '#4caf50',
                      fontWeight: totalBalance > 0 ? 'bold' : 'normal',
                    }}>
                      {formatCurrency(totalBalance)}
                    </td>
                    <td style={{ padding: '12px 8px' }}>{new Date(client.createdAt).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};
